import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Loader from "../components/Loader";
import authService from "../services/authService";
import dealerService from "../services/dealerService";

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TransferDealer = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(authService.getCurrentUser());
  const [dealers, setDealers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transferLoading, setTransferLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [transferForm, setTransferForm] = useState({
    dealer_id: location.state?.dealer?.id ? String(location.state.dealer.id) : "",
    amount: "",
  });
  const [generateForm, setGenerateForm] = useState({ amount: "", note: "" });

  const loadData = async () => {
    try {
      setLoading(true);
      const [dealerData, hierarchyData] = await Promise.all([
        dealerService.getDealers(),
        dealerService.getHierarchy(),
      ]);
      setDealers(Array.isArray(dealerData?.dealers) ? dealerData.dealers : []);
      setTransactions(Array.isArray(hierarchyData?.hierarchy) ? hierarchyData.hierarchy : []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load coin management data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const selectedDealer = useMemo(
    () => dealers.find((dealer) => String(dealer.id) === String(transferForm.dealer_id)),
    [dealers, transferForm.dealer_id]
  );

  const activeDealers = dealers.filter((dealer) => dealer.status === "ACTIVE");
  const dealerWalletTotal = dealers.reduce((sum, dealer) => sum + Number(dealer.wallet || 0), 0);

  const saveAdminWallet = (wallet) => {
    const updatedUser = { ...(user || {}), wallet: Number(wallet) };
    authService.setCurrentUser(updatedUser);
    setUser(updatedUser);
  };

  const generateCoins = async (event) => {
    event.preventDefault();
    const amount = Number(generateForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) return toast.error("Enter a valid coin amount.");

    try {
      setGenerateLoading(true);
      const data = await dealerService.generateCoins({ amount, note: generateForm.note.trim() });
      if (data?.wallet !== undefined) saveAdminWallet(data.wallet);
      toast.success(data?.message || "Coins generated successfully.");
      setGenerateForm({ amount: "", note: "" });
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to generate coins.");
    } finally {
      setGenerateLoading(false);
    }
  };

  const transferCoins = async (event) => {
    event.preventDefault();
    const dealerId = Number(transferForm.dealer_id);
    const amount = Number(transferForm.amount);

    if (!Number.isInteger(dealerId) || dealerId <= 0) return toast.error("Select a Dealer.");
    if (!Number.isFinite(amount) || amount <= 0) return toast.error("Enter a valid transfer amount.");
    if (!selectedDealer || selectedDealer.status !== "ACTIVE") return toast.error("Select an active Dealer.");
    if (amount > Number(user?.wallet || 0)) return toast.error("Insufficient Super Admin wallet balance.");

    try {
      setTransferLoading(true);
      const data = await dealerService.transferToDealer({ dealer_id: dealerId, amount });
      const balances = data?.balances || {};
      if (balances.super_admin_wallet !== undefined) saveAdminWallet(balances.super_admin_wallet);
      if (balances.dealer_wallet !== undefined) {
        setDealers((current) => current.map((dealer) => Number(dealer.id) === dealerId ? { ...dealer, wallet: balances.dealer_wallet } : dealer));
      }
      toast.success(data?.message || "Coins transferred successfully.");
      setTransferForm((current) => ({ ...current, amount: "" }));
    } catch (error) {
      toast.error(error.response?.data?.message || "Coin transfer failed.");
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">
      <Navbar toggleSidebar={() => setSidebarOpen((value) => !value)} />
      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar isOpen={sidebarOpen} closeMobileSidebar={() => setSidebarOpen(false)} />
        <main className="app-main flex-grow-1 overflow-auto">
          <div className="container-fluid admin-page-wrap">
            <div className="admin-page-header">
              <div>
                <span className="admin-page-kicker">COIN CONTROL</span>
                <h2>Super Admin Coin Management</h2>
                <p>Generate system coins and distribute balance to active Dealers.</p>
              </div>
              <button className="btn btn-outline-primary" onClick={loadData} disabled={loading}>
                <i className="bi bi-arrow-clockwise me-2" /> Refresh
              </button>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-6 col-xl-3"><div className="admin-stat-card h-100"><span className="admin-stat-icon tone-success"><i className="bi bi-wallet2" /></span><div><div className="admin-stat-label">Admin Wallet</div><div className="admin-stat-value">🪙 {money(user?.wallet)}</div></div></div></div>
              <div className="col-6 col-xl-3"><div className="admin-stat-card h-100"><span className="admin-stat-icon tone-primary"><i className="bi bi-people-fill" /></span><div><div className="admin-stat-label">Active Dealers</div><div className="admin-stat-value">{activeDealers.length}</div></div></div></div>
              <div className="col-6 col-xl-3"><div className="admin-stat-card h-100"><span className="admin-stat-icon tone-warning"><i className="bi bi-coin" /></span><div><div className="admin-stat-label">Dealer Wallet Total</div><div className="admin-stat-value">🪙 {money(dealerWalletTotal)}</div></div></div></div>
              <div className="col-6 col-xl-3"><div className="admin-stat-card h-100"><span className="admin-stat-icon tone-info"><i className="bi bi-person-check-fill" /></span><div><div className="admin-stat-label">Selected Dealer</div><div className="admin-stat-value admin-stat-value-sm">{selectedDealer?.username || "None"}</div></div></div></div>
            </div>

            {loading ? <div className="admin-panel p-5"><Loader message="Loading coin management..." /></div> : (
              <div className="row g-4">
                <div className="col-12 col-xl-5">
                  <div className="admin-panel h-100">
                    <div className="admin-panel-head"><div><h5>Generate Coins</h5><p>Mint coins directly into the Super Admin wallet.</p></div><span className="admin-panel-icon success"><i className="bi bi-plus-circle-fill" /></span></div>
                    <form className="admin-form" onSubmit={generateCoins}>
                      <label>Coin Amount</label>
                      <div className="admin-input-group"><i className="bi bi-coin" /><input type="number" min="0.01" step="0.01" value={generateForm.amount} onChange={(event) => setGenerateForm((current) => ({ ...current, amount: event.target.value }))} placeholder="Enter coin amount" required /></div>
                      <label>Transaction Note</label>
                      <textarea rows="4" maxLength="255" value={generateForm.note} onChange={(event) => setGenerateForm((current) => ({ ...current, note: event.target.value }))} placeholder="Optional note for audit trail" />
                      <button className="btn btn-success admin-primary-btn" disabled={generateLoading}>{generateLoading ? "Generating..." : "Generate Coins"}</button>
                    </form>
                  </div>
                </div>

                <div className="col-12 col-xl-7">
                  <div className="admin-panel h-100">
                    <div className="admin-panel-head"><div><h5>Transfer to Dealer</h5><p>Move coins from the Super Admin wallet to a selected Dealer.</p></div><span className="admin-panel-icon primary"><i className="bi bi-send-fill" /></span></div>
                    <form className="admin-form" onSubmit={transferCoins}>
                      <label>Dealer</label>
                      <select value={transferForm.dealer_id} onChange={(event) => setTransferForm((current) => ({ ...current, dealer_id: event.target.value }))} required>
                        <option value="">Select active Dealer</option>
                        {dealers.map((dealer) => <option key={dealer.id} value={dealer.id} disabled={dealer.status !== "ACTIVE"}>{dealer.full_name || dealer.username} (@{dealer.username}) · 🪙 {money(dealer.wallet)} {dealer.status !== "ACTIVE" ? "· BLOCKED" : ""}</option>)}
                      </select>

                      {selectedDealer && <div className="selected-dealer-card"><div><span className="hierarchy-avatar">{(selectedDealer.full_name || selectedDealer.username).charAt(0).toUpperCase()}</span><div><strong>{selectedDealer.full_name}</strong><small>@{selectedDealer.username} · #{selectedDealer.id}</small></div></div><div><small>Current Wallet</small><strong>🪙 {money(selectedDealer.wallet)}</strong></div></div>}

                      <label>Transfer Amount</label>
                      <div className="admin-input-group"><i className="bi bi-wallet2" /><input type="number" min="0.01" step="0.01" value={transferForm.amount} onChange={(event) => setTransferForm((current) => ({ ...current, amount: event.target.value }))} placeholder="Enter transfer amount" required /></div>
                      <div className="transfer-balance-note"><span>Available Admin Balance</span><strong>🪙 {money(user?.wallet)}</strong></div>
                      <button className="btn btn-primary admin-primary-btn" disabled={transferLoading || selectedDealer?.status === "BLOCKED"}>{transferLoading ? "Transferring..." : "Transfer Coins"}</button>
                    </form>
                  </div>
                </div>

                <div className="col-12">
                  <div className="admin-panel">
                    <div className="admin-panel-head"><div><h5>Dealer Wallet Overview</h5><p>Quick balance and network context before making transfers.</p></div></div>
                    <div className="table-responsive"><table className="table admin-table align-middle mb-0"><thead><tr><th>Dealer</th><th>Status</th><th>Shops</th><th>Computers</th><th className="text-end">Wallet</th></tr></thead><tbody>{transactions.map((dealer) => <tr key={dealer.id}><td><strong>{dealer.full_name || dealer.username}</strong><small>@{dealer.username} · #{dealer.id}</small></td><td><span className={`status-pill ${dealer.status === "ACTIVE" ? "active" : "blocked"}`}>{dealer.status}</span></td><td>{dealer.total_shops || 0}</td><td>{dealer.total_computers || dealer.total_players || 0}</td><td className="text-end fw-bold text-success">🪙 {money(dealer.wallet)}</td></tr>)}</tbody></table></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TransferDealer;
